import React, { useState } from "react";
import styles from "./styles/BookingTable.module.css";

export interface BookingTableProps<T> {
    data: T[];
    columns: {
        title: string;
        dataIndex: keyof T;
        render?: (value: any, record: T) => React.ReactNode;
    }[];
    rowKey: keyof T;
    searchFields?: (keyof T)[];
    statusField?: keyof T;
}

const STATUS_OPTIONS = [
    "All",
    "PENDENTE",
    "PAGO",
    "CANCELADO",
    "CONCLUIDO",
    "EM_ANDAMENTO"
];

function BookingTable<T extends Record<string, any>>({
    data,
    columns,
    rowKey,
    searchFields,
    statusField,
}: BookingTableProps<T>) {
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("All");

    const filteredData = data.filter((item) => {
        const matchesSearch =
            !search ||
            (searchFields &&
                searchFields.some((field) =>
                    item[field]?.toString().toLowerCase().includes(search.toLowerCase())
                ));

        const matchesStatus =
            !statusField ||
            statusFilter === "All" ||
            item[statusField]?.toString().toUpperCase() === statusFilter.toUpperCase();

        return matchesSearch && matchesStatus;
    });

    const getStatusBadge = (status: string) => {
        let badgeClass = styles.statusCompleted; // padrão

        switch (status.toUpperCase()) {
            case "PENDENTE":
                badgeClass = styles.statusPending;
                break;
            case "PAGO":
                badgeClass = styles.statusPaid;
                break;
            case "CANCELADO":
                badgeClass = styles.statusCancelled;
                break;
            case "CONCLUIDO":
                badgeClass = styles.statusCompleted;
                break;
            case "EM_ANDAMENTO":
                badgeClass = styles.statusInProgress;
                break;
            default:
                badgeClass = "";
        }

        return <span className={`${styles.statusBadge} ${badgeClass}`}>{status}</span>;
    };

    return (
        <div className={styles.container}>
            <div className={styles.filters}>
                {searchFields && (
                    <input
                        type="text"
                        className={styles.filterInput}
                        placeholder={`Search by ${searchFields.map(f => f.toString()).join(", ")}`}
                        title={`Type to search by: ${searchFields.map(f => f.toString()).join(", ")}`}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                )}
                {statusField && (
                    <select
                        className={styles.filterSelect}
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        {STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>
                                {status.replace("_", " ")}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead className={styles.thead}>
                        <tr>
                            {columns.map((col) => (
                                <th key={col.title} className={styles.th}>
                                    {col.title}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.length === 0 && (
                            <tr>
                                <td colSpan={columns.length} className={styles.noData}>
                                    No bookings found.
                                </td>
                            </tr>
                        )}
                        {filteredData.map((item, idx) => (
                            <tr
                                key={item[rowKey]}
                                className={idx % 2 === 0 ? styles.trOdd : styles.trEven}
                            >
                                {columns.map((col) => (
                                    <td
                                        key={col.title}
                                        className={styles.td}
                                        data-title={col.title}
                                    >
                                        {col.render
                                            ? col.render(item[col.dataIndex], item)
                                            : col.dataIndex === statusField
                                                ? getStatusBadge(item[col.dataIndex])
                                                : item[col.dataIndex]}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default BookingTable;